import { useEffect, useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Controller, useForm } from 'react-hook-form';
import { Alert, Button, Checkbox, Form, Label, Select, TextArea } from '@/shared/components';
import { PermissionGuard, usePermissions } from '@/features/auth';
import { isApiError, isNotFoundError } from '@/services/api';
import type { Product } from '@/features/products/types/products.types';
import { resolveProductCampaign } from '../services/campaign';
import { useCampaignBannerRules } from '../hooks/useCampaignBannerRules';
import { useConfigOptionsQuery } from '../hooks/useConfigOptionsQuery';
import { settingsToBannerForm, bannerFormToSettings } from '../services/bannerRules';
import { makeBannerRulesSchema, BANNER_SEVERITY_FALLBACK } from '../schemas/bannerRules.schema';
import type { BannerRulesFormValues } from '../schemas/bannerRules.schema';

interface Props {
  productId: string;
  product: Product;
}

const resolveCampaignKeys = {
  forProduct: (productId: string) => ['smr-admin', 'campaign', 'resolve', productId] as const,
};

const DEFAULT_VALUES: BannerRulesFormValues = {
  banner_enabled: false,
  banner_severity: '',
  banner_text: '',
};

const capitalise = (value: string) => (value.length ? value[0].toUpperCase() + value.slice(1) : value);

/**
 * Bespoke Banner Rules tab body. Mirrors CampaignTransferRulePanel's resolve-then-fetch
 * two-query structure, but drives a react-hook-form + Zod form over the three banner_*
 * config-setting keys instead of a dedicated resource endpoint (the storage stays the
 * existing campaign_config_settings + attach endpoint — see plan Decision D5).
 */
export function CampaignBannerPanel({ productId, product }: Props) {
  const resolveQuery = useQuery({
    queryKey: resolveCampaignKeys.forProduct(productId),
    queryFn: () => resolveProductCampaign(productId),
  });

  const campaignId = resolveQuery.data?.campaign_id;

  const { query: rulesQuery, saveMutation } = useCampaignBannerRules(campaignId);

  // Severity enum + per-key config_option_id sourced from the catalogue (Decision D3).
  const catalogQuery = useConfigOptionsQuery({ group: 'general' });
  const catalogOptions = catalogQuery.data?.data ?? [];
  const optionIdByKey = Object.fromEntries(catalogOptions.map((o) => [o.key_name, o.id]));
  const severityOption = catalogOptions.find((o) => o.key_name === 'banner_severity');
  const severityValues = useMemo(
    () => (severityOption?.validation_rules?.in as string[] | undefined) ?? [...BANNER_SEVERITY_FALLBACK],
    [severityOption],
  );
  const severityOptions = useMemo(
    () => severityValues.map((value) => ({ value, label: capitalise(value) })),
    [severityValues],
  );

  // Rebuild the Zod schema from the LIVE allowed-severity set so an enabled banner with a
  // stale/narrowed/invalid severity fails validation at the field (not only at the backend
  // Rule::in). Before the catalogue loads this uses BANNER_SEVERITY_FALLBACK.
  const schema = useMemo(() => makeBannerRulesSchema(severityValues), [severityValues]);

  const isRetired = Boolean(product.deleted_at);

  // The form itself is NOT wrapped in PermissionGuard, so a user with only
  // campaign.read still sees the current banner (matches the transfer panel's
  // read-only-view-without-guard convention). Individual controls are disabled for
  // that case; only the Save action is additionally gated by PermissionGuard below.
  // This is a UX convenience — the real authority is the backend's
  // permission:campaign.edit + audit middleware on the POST attach endpoint.
  const { hasPermission } = usePermissions();
  const canEdit = hasPermission({ resource: 'campaign', action: 'edit' });
  const controlsDisabled = isRetired || !canEdit;

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    setValue,
    formState: { isDirty, isSubmitting },
  } = useForm<BannerRulesFormValues>({
    resolver: zodResolver(schema),
    defaultValues: DEFAULT_VALUES,
  });

  // Seed the form from the loaded settings. Only re-seed when the query data identity
  // changes AND the form is not dirty, so a background refetch cannot clobber an
  // in-progress edit (mirrors the transfer panel's R4 guard). Post-save re-seeding is
  // handled explicitly in onSubmit's onSuccess (which clears dirty first) so the guard
  // here does not block the after-save refetch.
  useEffect(() => {
    if (!rulesQuery.data) return;
    if (isDirty) return;
    reset(settingsToBannerForm(rulesQuery.data));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rulesQuery.data]);

  const enabled = watch('banner_enabled');

  const onSubmit = (values: BannerRulesFormValues) => {
    saveMutation.mutate(bannerFormToSettings(values, optionIdByKey), {
      // Re-seed the form from the server-normalized response on a successful save. This
      // clears the dirty state, so the form is not left permanently dirty and the
      // background-refetch guard below (which early-returns while isDirty) can re-seed on
      // subsequent refetches. Without this, the post-save invalidation's refetch would
      // hit the isDirty guard and reset() would never run.
      onSuccess: (saved) => reset(settingsToBannerForm(saved)),
    });
  };

  if (resolveQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading banner rules…</p>;
  }

  const noCampaignAlert = (
    <Alert variant="warning">
      <span>This product has no campaign yet. Banner rules are unavailable until one is provisioned.</span>
    </Alert>
  );

  // The product->campaign resolver returns a 404 for an unprovisioned product, so a real
  // "no campaign yet" case surfaces as a 404-shaped ApiError here, not as a 200 with an
  // empty campaign_id. Handle it before the generic error branch so the friendly warning
  // renders instead of the scary "Failed to load" alert. All other resolver errors still
  // fall through to the generic error alert below.
  if (isNotFoundError(resolveQuery.error)) {
    return noCampaignAlert;
  }

  if (resolveQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load this product&apos;s campaign. Banner rules are unavailable.</span>
      </Alert>
    );
  }

  // Belt-and-braces: also cover a 200-with-empty-campaign_id shape.
  if (!campaignId) {
    return noCampaignAlert;
  }

  if (rulesQuery.isLoading) {
    return <p className="text-sm text-mp-muted">Loading banner rules…</p>;
  }

  if (rulesQuery.isError) {
    return (
      <Alert variant="error">
        <span>Failed to load banner rules.</span>
      </Alert>
    );
  }

  return (
    <Form onSubmit={handleSubmit(onSubmit)} className="space-y-6 max-w-2xl">
      <div>
        <Controller
          name="banner_enabled"
          control={control}
          render={({ field }) => (
            <Checkbox
              id="banner_enabled"
              label="Banner Enabled"
              ariaLabel="Banner Enabled"
              checked={field.value}
              onChange={(checked) => {
                field.onChange(checked);
                // Default severity to the first option when enabling with none
                // chosen, so a Select always yields a value (no friction).
                if (checked && !getValues('banner_severity') && severityOptions.length > 0) {
                  setValue('banner_severity', severityOptions[0].value);
                }
              }}
              disabled={controlsDisabled}
            />
          )}
        />
      </div>

      {enabled && (
        <>
          <div>
            <Label htmlFor="banner_severity">Severity</Label>
            <Controller
              name="banner_severity"
              control={control}
              render={({ field, fieldState }) => (
                <Select
                  id="banner_severity"
                  value={field.value}
                  onChange={field.onChange}
                  onBlur={field.onBlur}
                  options={severityOptions}
                  // The visible <Label htmlFor> does not associate with
                  // Select's combobox div, which derives its accessible
                  // name from placeholder — give it a distinct name.
                  placeholder="Severity"
                  disabled={controlsDisabled}
                  error={!!fieldState.error}
                  hint={fieldState.error?.message}
                />
              )}
            />
          </div>
          <div>
            <Label htmlFor="banner_text">Text</Label>
            <Controller
              name="banner_text"
              control={control}
              render={({ field, fieldState }) => (
                <TextArea
                  id="banner_text"
                  ariaLabel="Text"
                  value={field.value}
                  onChange={field.onChange}
                  rows={4}
                  disabled={controlsDisabled}
                  error={!!fieldState.error}
                  hint={fieldState.error?.message}
                />
              )}
            />
          </div>
        </>
      )}

      {saveMutation.isError && (
        <Alert variant="error">
          <span>
            {isApiError(saveMutation.error) ? saveMutation.error.message : 'Failed to save banner rules.'}
          </span>
        </Alert>
      )}

      {!isRetired && (
        <PermissionGuard permission={{ resource: 'campaign', action: 'edit' }}>
          <Button size="sm" disabled={isSubmitting || saveMutation.isPending}>
            {saveMutation.isPending ? 'Saving…' : 'Save'}
          </Button>
        </PermissionGuard>
      )}
    </Form>
  );
}
